import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { clientPortalService } from '../../services/clientPortalService';
import WelcomeSection from './components/WelcomeSection';
import MaturityScoreCard from './components/MaturityScoreCard';
import ConsultationHistory from './components/ConsultationHistory';
import AssessmentResults from './components/AssessmentResults';
import RecommendationsEngine from './components/RecommendationsEngine';
import ProgressTracking from './components/ProgressTracking';

const ClientPortalDashboard = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeSection, setActiveSection] = useState('overview');

  useEffect(() => {
    if (!user) {
      navigate('/signin');
      return;
    }

    loadDashboardData();
  }, [user, navigate]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const data = await clientPortalService?.getClientDashboardSummary(user?.id);
      setDashboardData(data);
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadDashboardData();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#002B45] via-[#003D5C] to-[#002B45]">
        <Header />
        <div className="pt-24 pb-16 px-4">
          <div className="max-w-7xl mx-auto text-center">
            <Icon name="Loader2" size={48} className="animate-spin text-[#1FB5A5] mx-auto" />
            <p className="mt-4 text-white">Loading your dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#002B45] via-[#003D5C] to-[#002B45]">
        <Header />
        <div className="pt-24 pb-16 px-4">
          <div className="max-w-7xl mx-auto text-center">
            <Icon name="AlertCircle" size={48} className="text-red-500 mx-auto" />
            <p className="mt-4 text-red-400">{error}</p>
            <Button onClick={handleRefresh} className="mt-4">
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const sections = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'consultations', label: 'Consultation History', icon: 'Calendar' },
    { id: 'assessments', label: 'Assessment Results', icon: 'FileText' },
    { id: 'recommendations', label: 'Recommendations', icon: 'Lightbulb' },
    { id: 'progress', label: 'Progress Tracking', icon: 'TrendingUp' },
    { id: 'ai-recommendations', label: 'AI Recommendations', icon: 'Sparkles' }
  ];

  return (
    <>
      <Helmet>
        <title>Client Portal Dashboard | Northstar Data Strategy</title>
        <meta
          name="description"
          content="Access your personalized data maturity insights, consultation history, and strategic recommendations."
        />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-[#002B45] via-[#003D5C] to-[#002B45]">
        <Header />

        <div className="pt-24 pb-16 px-4">
          <div className="max-w-7xl mx-auto">
            {/* Welcome Section */}
            <WelcomeSection
              userName={userProfile?.full_name || 'Valued Client'}
              company={userProfile?.company}
            />

            {/* Maturity Score Card */}
            <MaturityScoreCard
              currentScore={dashboardData?.currentMaturityScore}
              trend={dashboardData?.maturityTrend}
              latestScores={dashboardData?.latestScores}
            />

            {/* Section Navigation */}
            <div className="mb-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-2 flex flex-wrap gap-2">
                {sections?.map((section) => (
                  <button
                    key={section?.id}
                    onClick={() => setActiveSection(section?.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300 ${
                      activeSection === section?.id
                        ? 'bg-[#1FB5A5] text-white shadow-lg'
                        : 'text-white hover:bg-white/10'
                    }`}
                  >
                    <Icon name={section?.icon} size={18} />
                    <span className="text-sm font-medium">{section?.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Dynamic Content Sections */}
            {activeSection === 'overview' && (
              <div className="space-y-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <ConsultationHistory
                    consultations={dashboardData?.consultations?.slice(0, 3)}
                    totalCount={dashboardData?.totalConsultations}
                    onViewAll={() => setActiveSection('consultations')}
                  />
                  <RecommendationsEngine
                    clientId={user?.id}
                    recommendations={dashboardData?.recommendations?.slice(0, 3)}
                    totalCount={dashboardData?.activeRecommendations}
                    onViewAll={() => setActiveSection('recommendations')}
                    onRefresh={handleRefresh}
                  />
                </div>
                <ProgressTracking
                  progressData={dashboardData?.progressData}
                  latestScores={dashboardData?.latestScores}
                  compact={true}
                />
              </div>
            )}

            {activeSection === 'consultations' && (
              <ConsultationHistory
                consultations={dashboardData?.consultations}
                totalCount={dashboardData?.totalConsultations}
                fullView={true}
                onViewAll={() => {}}
              />
            )}

            {activeSection === 'assessments' && (
              <AssessmentResults
                assessments={dashboardData?.assessments}
                onNavigateToHistory={() => navigate('/assessment-history-analytics-progress-tracking-center')}
              />
            )}

            {activeSection === 'recommendations' && (
              <RecommendationsEngine
                clientId={user?.id}
                recommendations={dashboardData?.recommendations}
                totalCount={dashboardData?.activeRecommendations}
                fullView={true}
                onRefresh={handleRefresh}
                onViewAll={() => {}}
              />
            )}

            {activeSection === 'progress' && (
              <ProgressTracking
                progressData={dashboardData?.progressData}
                latestScores={dashboardData?.latestScores}
                compact={false}
              />
            )}

            {activeSection === 'ai-recommendations' && (
              <div className="bg-white rounded-xl shadow-elevation-2 p-8 text-center">
                <div className="w-20 h-20 bg-[#1FB5A5]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Icon name="Sparkles" size={40} className="text-[#1FB5A5]" />
                </div>
                <h3 className="text-2xl font-bold text-[#002B45] mb-3">
                  AI-Powered Service Recommendations
                </h3>
                <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                  Get personalized service recommendations with sophisticated ROI predictions powered by advanced AI analysis of your data maturity profile.
                </p>
                <Button
                  onClick={() => navigate('/ai-powered-service-recommendations-intelligent-roi-prediction-center')}
                  iconName="Sparkles"
                  iconPosition="left"
                  size="lg"
                  className="bg-[#1FB5A5] hover:bg-[#1FB5A5]/90"
                >
                  Explore AI Recommendations
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ClientPortalDashboard;